import React, {useEffect, useMemo, useRef, useState} from 'react';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import {Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Typography} from '@mui/material';
import { ChartBarProductsExtendedProps, ViewTypes } from './types';
import {
    getTooltipCallbacks,
    transformData,
    convertUnits,
    useGetFilteredItemsByClass
} from './Chart.Common';
import { Chart as ChartJS, ChartOptions } from 'chart.js';
import annotationPlugin, { LineAnnotationOptions } from 'chartjs-plugin-annotation';
ChartJS.register(annotationPlugin);

const ChartBarProducts: React.FC<ChartBarProductsExtendedProps> = ({
                                                                       data,
                                                                       selectedProducts,
                                                                       setSelectedProducts,
                                                                       compoundFilter,
                                                                       setCompoundFilter,
                                                                       showNonNumeric,
                                                                       setShowNonNumeric,
                                                                       selectedClass,
                                                                       setSelectedClass,
                                                                       selectedUnit,
                                                                       setSelectedUnit,
                                                                       selectedDenominator,
                                                                       setSelectedDenominator,
                                                                       selectedDiseaseCategory,
                                                                       setSelectedDiseaseCategory,
                                                                       diseaseInchiKeys,
                                                                       setDiseaseInchiKeys,
                                                                       selectedCompounds,
                                                                       setSelectedCompounds,
                                                                       selectedAnalysisType,
                                                                       setSelectedAnalysisType
                                                                   }) => {
    const chartRef = useRef<ChartJS<'bar'> | null>(null);
    const [visualizationMode, setVisualizationMode] = useState<ViewTypes>(ViewTypes.DIRECT);
    const filteredItems = useMemo(() => data.filter(item => item.HEADER === "List of Products Analysis Values"), [data]);
    const handleModeChange = (event: SelectChangeEvent<ViewTypes>) => {
        setVisualizationMode(event.target.value as ViewTypes);
    };
    const classFilteredItems = useGetFilteredItemsByClass(filteredItems, selectedClass);
    const analysisFilteredItems = useMemo(() => {
        if (selectedAnalysisType === 'All') return classFilteredItems;
        return classFilteredItems.map(item => ({
            ...item,
            DATA: item.DATA.filter((row: any) => row.ANALYSIS_TYPE === selectedAnalysisType)
        })).filter(item => item.DATA.length > 0);
    }, [classFilteredItems, selectedAnalysisType]);
    const convertedItems = useMemo(() => {
        return convertUnits(analysisFilteredItems, selectedUnit, selectedDenominator);
    }, [analysisFilteredItems, selectedUnit, selectedDenominator]);
    const processedItems = useMemo(() => {
        if (selectedProducts.length === 0) return convertedItems;
        const productItems: any[] = [];
        convertedItems.forEach(item => {
            const productDataMap = new Map<string, any[]>();
            item.DATA.forEach((row: any) => {
                const key = `${row.PRODUCT || 'Unknown'} (${row.ANALYSIS_TYPE || 'Unknown'})`;
                if (selectedProducts.includes(key)) {
                    if (!productDataMap.has(key)) {
                        productDataMap.set(key, []);
                    }
                    productDataMap.get(key)!.push(row);
                }
            });
            productDataMap.forEach((data, key) => {
                productItems.push({
                    ...item,
                    DATA: data,
                    TITLE: key
                });
            });
        });
        console.log('Processed Items:', productItems);
        return productItems;
    }, [convertedItems, selectedProducts]);
    const chartData = useMemo(() => {
        const transformed = transformData(
            processedItems,
            selectedProducts,
            visualizationMode,
            (item) => `${item.DATA[0]?.PRODUCT || 'Unknown'} (${item.DATA[0]?.ANALYSIS_TYPE || 'Unknown'})`,
            (row) => Number(row.AVERAGE) || 0,
            (row) => row.COMPOUND_NAME,
            (row) => Number(row.STD_DEV) || null,
            (item) => item.DATA[0]?.UNIT || `${selectedUnit}/${selectedDenominator}`,
            compoundFilter,
            showNonNumeric,
            diseaseInchiKeys
        );
        // Apply compound filter
        if (selectedCompounds.length > 0 && !selectedCompounds.includes('All')) {
            return {
                ...transformed,
                labels: transformed.labels.filter(label => selectedCompounds.includes(label)),
                datasets: transformed.datasets.map(dataset => ({
                    ...dataset,
                    data: dataset.data.filter((_, idx) => selectedCompounds.includes(transformed.labels[idx])),
                    extras: Object.fromEntries(
                        Object.entries(dataset.extras || {}).filter(([compound]) => selectedCompounds.includes(compound))
                    )
                }))
            };
        }
        return transformed;
    }, [processedItems, selectedProducts, visualizationMode, compoundFilter, showNonNumeric, selectedUnit, selectedDenominator, diseaseInchiKeys, selectedCompounds]);
    console.log(' ----------- chartData ', chartData);
    const annotations = useMemo(() => {
        if (visualizationMode === ViewTypes.NORMALIZED || visualizationMode === ViewTypes.LOGARITHMIC) return {};
        const numDatasets = chartData.datasets.length;
        const barWidthFraction = numDatasets > 1 ? 0.8 / numDatasets : 0.8;
        return chartData.datasets.reduce((acc, dataset, datasetIndex) => {
            chartData.labels.forEach((label, index) => {
                const stdev = dataset.errorBars?.[label]?.plus || 0;
                if (stdev) {
                    const value = dataset.data[index];
                    let yMin: number, yMax: number;
                    switch (visualizationMode) {
                        case ViewTypes.DIRECT:
                            yMin = value - stdev;
                            yMax = value + stdev;
                            break;
                        case ViewTypes.PERCENTAGE:
                            const totalForProduct = processedItems
                                .find(item => item.TITLE === dataset.label)
                                ?.DATA.reduce((sum: number, row: any) => sum + (Number(row.AVERAGE) || 0), 0) || 1;
                            const stdevPercentage = totalForProduct > 0 ? (stdev / totalForProduct) * 100 : 0;
                            yMin = value - stdevPercentage;
                            yMax = value + stdevPercentage;
                            break;
                        default:
                            yMin = value - stdev;
                            yMax = value + stdev;
                    }
                    const xPosition = numDatasets > 1
                        ? index + ((datasetIndex - (numDatasets - 1) / 2) * barWidthFraction)
                        : index;
                    const annotationId = `errorBar-${datasetIndex}-${index}`;
                    acc[annotationId] = {
                        type: 'line' as const,
                        xMin: xPosition,
                        xMax: xPosition,
                        yMin,
                        yMax,
                        borderColor: 'rgba(0, 0, 0, 1)',
                        borderWidth: 1,
                    } as LineAnnotationOptions;
                }
            });
            return acc;
        }, {} as { [key: string]: LineAnnotationOptions });
    }, [chartData, visualizationMode, processedItems]);
    const options: ChartOptions<'bar'> = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top' as const,
                onClick: () => {},
            },
            title: {
                display: true,
                text: `Compound Values by Product (${visualizationMode.charAt(0).toUpperCase() + visualizationMode.slice(1)}) (${selectedUnit}/${selectedDenominator})`,
            },
            tooltip: {
                callbacks: getTooltipCallbacks(
                    processedItems,
                    (item) => `${item.DATA[0]?.PRODUCT || 'Unknown'} (${item.DATA[0]?.ANALYSIS_TYPE || 'Unknown'})`,
                    (row) => row.COMPOUND_NAME,
                    (row) => Number(row.AVERAGE) || 0,
                    (row) => Number(row.STD_DEV) || null,
                    (item) => item.DATA[0]?.UNIT || `${selectedUnit}/${selectedDenominator}`,
                    visualizationMode
                ),
            },
            annotation: {
                annotations: annotations
            }
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Compounds',
                },
            },
            y: {
                title: {
                    display: true,
                    text: visualizationMode === ViewTypes.PERCENTAGE ? '% of Product Total' :
                        visualizationMode === ViewTypes.NORMALIZED ? 'Normalized Value (%)' :
                            visualizationMode === ViewTypes.LOGARITHMIC ? 'Log10 Normalized (%)' :
                                `Value (${selectedUnit}/${selectedDenominator})`,
                },
                beginAtZero: true,
            },
        },
    };
    useEffect(() => {
        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, []);
    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Visualization Mode</InputLabel>
                    <Select
                        value={visualizationMode}
                        onChange={handleModeChange}
                        label="Visualization Mode"
                        size="small"
                    >
                        <MenuItem value={ViewTypes.DIRECT}>Direct Value</MenuItem>
                        <MenuItem value={ViewTypes.PERCENTAGE}>% Range</MenuItem>
                        <MenuItem value={ViewTypes.LOGARITHMIC}>Logarithmic Scale</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                {analysisFilteredItems.length > 0 && chartData.labels.length > 0 ? (
                    <Box sx={{ width: '80%' }}>
                        <Bar
                            ref={chartRef}
                            data={chartData}
                            options={options}
                        />
                    </Box>
                ) : (
                    <Typography>No valid product analysis data available for selected filters.</Typography>
                )}
            </Box>
        </Box>
    );
};

export default ChartBarProducts;
